import { z } from 'zod';

// "Online now" and "visitors" (real people who have ever opened the game, signed up or not) on the homepage.
// The real numbers are counted by the server; the super admin decides whether the public sees them, and may
// show their own numbers instead (they still see the real ones).
export const SiteStatsSettings = z
  .object({
    showPublic: z.boolean(),
    mode: z.enum(['real', 'custom']),
    customOnline: z.number().int().min(0).max(10_000_000),
    customPeople: z.number().int().min(0).max(1_000_000_000),
  })
  .strict();
export type SiteStatsSettings = z.infer<typeof SiteStatsSettings>;
export const DEFAULT_SITE_STATS: SiteStatsSettings = {
  showPublic: true,
  mode: 'real',
  customOnline: 0,
  customPeople: 0,
};

// what the homepage gets: nothing at all when hidden. People is all time: every different person who has
// opened the game since launch, whether or not they signed up
export const PublicStatsReply = z.object({
  stats: z.object({ show: z.boolean(), online: z.number().int().nullable(), people: z.number().int().nullable() }),
});
export type PublicStatsReply = z.infer<typeof PublicStatsReply>;

// what the super admin sees: the settings, and always the real numbers
// (peopleTotal = unique people all time; visitors = unique people per day; views = openings of the game)
export const RealSiteStats = z.object({
  onlineNow: z.number().int(),
  peopleTotal: z.number().int(),
  visitorsToday: z.number().int(),
  viewsToday: z.number().int(),
  viewsTotal: z.number().int(),
  days: z.array(z.object({ day: z.string(), visitors: z.number().int(), views: z.number().int() })),
});
export type RealSiteStats = z.infer<typeof RealSiteStats>;
export const AdminStatsReply = z.object({ settings: SiteStatsSettings, real: RealSiteStats });
export type AdminStatsReply = z.infer<typeof AdminStatsReply>;

// a visitor counts as online for 2 minutes after their page last said hello; pages say hello every minute,
// and the first hello of a page open is a view
export const ONLINE_WINDOW_SECONDS = 120;
export const PRESENCE_EVERY_MS = 60_000;
export const Presence = z.object({ view: z.boolean().optional() }).strict();
export type Presence = z.infer<typeof Presence>;
